import { DEFAULT_PAX } from "@quote/consts";
import pluralize from "pluralize";
import { type StateCreator } from "zustand";

import {
  BED_CONFIG,
  MAX_PAX_PER_ROOM,
  MIN_TOTAL_PAX,
  MIN_PAX_PER_ROOM,
  MAX_TOTAL_PAX,
} from "./constants";
import { type RoomsState } from "./types";
import type { BedConfig, Room, RoomPax } from "./types";

const addOrRemovePax = (
  addOrRemove: number,
  index: number,
  rooms: Room[],
  error: string,
): Pick<RoomsState, "rooms"> => {
  const pax = rooms[index].pax + addOrRemove;
  const isPaxValid = pax >= MIN_PAX_PER_ROOM && pax <= MAX_PAX_PER_ROOM;

  if (isPaxValid) {
    return {
      rooms: rooms.map((room, i) =>
        i === index
          ? {
              ...room,
              bedConfig: BED_CONFIG.TWIN,
              pax: pax as RoomPax,
            }
          : room,
      ),
    };
  } else {
    throw newError(error);
  }
};

export const createRoomsFromPax = (pax: number): Room[] => {
  if (pax < MIN_TOTAL_PAX) return [];

  // 1-2 pax == 1 room, 3-4 pax == 2 rooms, etc.
  const roomCount = Math.ceil(pax / MAX_PAX_PER_ROOM);

  return Array.from({ length: roomCount }).map((_, i) => {
    // 1 pax == [1], 2 pax == [2], 3 pax == [2,1], 4 pax == [2,2], 5 pax == [2,2,1], etc.
    const roomPax = Math.min(
      MAX_PAX_PER_ROOM,
      pax - MAX_PAX_PER_ROOM * i,
    ) as RoomPax;

    return {
      bedConfig: BED_CONFIG.TWIN,
      pax: roomPax,
    };
  });
};

const newError = (error: string) => new Error(`ROOMS.${error}`);

export const createRoomsState: StateCreator<
  RoomsState,
  [["zustand/devtools", never]]
> = (set, get) => ({
  prevState: createRoomsFromPax(DEFAULT_PAX),
  rooms: createRoomsFromPax(DEFAULT_PAX),

  roomsActions: {
    addPaxAt: (index: number) =>
      set(
        (state) => addOrRemovePax(1, index, state.rooms, "MAX_PAX_REACHED"),
        undefined,
        "room/addPaxAt",
      ),

    addRoom: (isFourBall: boolean) => {
      if (get().roomsUtils.totalPax() < MAX_TOTAL_PAX) {
        const pax = isFourBall ? 4 : 1;
        set(
          { rooms: [...get().rooms, ...createRoomsFromPax(pax)] },
          undefined,
          "room/addRoom",
        );
      } else {
        throw newError("MAX_ROOMS_REACHED");
      }
    },

    removePaxAt: (index: number) =>
      set(
        (state) => addOrRemovePax(-1, index, state.rooms, "MIN_PAX_REACHED"),
        undefined,
        "room/removePaxAt",
      ),

    removeRoom: (index: number) => {
      const state = get();

      if (state.rooms.length > 1) {
        set(
          { rooms: state.rooms.filter((_, i) => i !== index) },
          undefined,
          "room/removeRoom",
        );
      } else {
        throw newError("MIN_ROOMS_REACHED");
      }
    },

    setBedConfigAt: (index: number, bedConfig: BedConfig) =>
      set(
        (state) => ({
          rooms: state.rooms.map((room, i) =>
            i === index ? { ...room, bedConfig } : room,
          ),
        }),
        undefined,
        "room/setBedConfigAt",
      ),

    setRoomsFromPax: (pax: number) =>
      set(
        { rooms: createRoomsFromPax(pax) },
        undefined,
        "room/setRoomsFromPax",
      ),

    setPrevState: () =>
      set(
        { prevState: get().rooms, rooms: get().rooms },
        undefined,
        "room/setPrevState",
      ),

    resetToPrev: () => {
      const prev = get().prevState;

      if (prev) {
        set({ prevState: null, rooms: prev }, undefined, "room/resetToPrev");
      }
    },
  },

  roomsUtils: {
    guestsCount: () => get().rooms.map((room) => room.pax),

    isMaxPaxAt: (index) => get().rooms[index].pax >= MAX_PAX_PER_ROOM,

    isMaxRooms: () => get().roomsUtils.totalPax() >= MAX_TOTAL_PAX,

    isMinPaxAt: (index) => get().rooms[index].pax <= MIN_PAX_PER_ROOM,

    isMinRooms: (fourBall: boolean) => get().rooms.length <= (fourBall ? 2 : 1),

    totalPax: () => get().rooms.reduce((acc, curr) => acc + curr.pax, 0),

    isQuartetGroup: () => {
      const totalPax = get().roomsUtils.totalPax();
      return totalPax > 0 && totalPax % 4 === 0;
    },

    generateSummaryText: () => {
      const { rooms, roomsUtils } = get();
      const totalPax = roomsUtils.totalPax();
      const totalRooms = rooms.length;

      return totalRooms > 0
        ? `${pluralize("Golfer", totalPax, true)}, ${pluralize("Room", totalRooms, true)}`
        : "";
    },
  },
});
